import type { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios'
import axios from 'axios'
import { Required } from 'utility-types'
import { nonNullable } from '@/types/typeguards'
import {
  CODE_API_SETUP_ERR,
  CODE_API_TIMEOUT_ERR,
  CODE_API_FAILED_TO_RESPOND,
  CODE_API_NON_200,
  CODE_API_UNKNOWN_ERROR,
} from '@/constants/api-error-codes'

export type AxiosOptionsForNetworkAbstraction = Required<
  AxiosRequestConfig,
  'url'
>

export default async function optimusPrime<
  ResponseReturnType extends Array<unknown> | object,
>(
  axiosOptions: AxiosOptionsForNetworkAbstraction,
): Promise<
  [
    AxiosResponse<ResponseReturnType>['data'] | null,
    typeof retError,
    {
      meta: AxiosOptionsForNetworkAbstraction
      errorResponse: AxiosResponse<unknown> | null
    },
  ]
> {
  let retVal: ResponseReturnType | null = null
  let retError: AxiosError | null = null
  let errorResponse: AxiosResponse<unknown> | null = null

  const finalAxiosOptions: AxiosOptionsForNetworkAbstraction = {
    timeout: 1500,
    ...axiosOptions,
  }

  try {
    const response = (await axios(
      finalAxiosOptions,
    )) as AxiosResponse<ResponseReturnType>

    if (
      (nonNullable(response.data) && response.status === 200) ||
      response.statusText === 'OK'
    ) {
      retVal = response.data
    }

    if (response.status === 200 && !nonNullable(response.data)) {
      console.error(
        `API returned with a 200 status but no data, API should have used 204 status code`,
        {
          url: finalAxiosOptions.url,
          options: finalAxiosOptions,
        },
      )
    }
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      console.warn(
        `${finalAxiosOptions.url}  Error: ${error} | API got cancelled because it didn't respond in ${finalAxiosOptions.timeout} milliseconds.`,
        {
          code: 'API_WARNING',
          subCode: CODE_API_FAILED_TO_RESPOND,
          TIMEOUT: finalAxiosOptions.timeout,
        },
      )
    } else if (axios.isAxiosError(error)) {
      // errors other than 2xx
      if (error.response) {
        console.error({
          url: finalAxiosOptions.url,
          error,
          message: error.message,
          code: 'API_ERROR',
          subCode: CODE_API_NON_200,
          statusCode: error.response.status,
          responseData: error.response.data,
          headers: error.response.headers,
        })

        errorResponse = error.response.data
      } else if (error.request) {
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
          console.error(`${finalAxiosOptions.url} timed out`, {
            error,
            code: 'API_ERROR',
            subCode: CODE_API_TIMEOUT_ERR,
            timeout: finalAxiosOptions.timeout,
          })
        }
      } else {
        // something happened in setting up the request that caused error
        console.error(
          `something went wrong in the setup of ${finalAxiosOptions.url}`,
          {
            error,
            code: 'API_ERROR',
            subCode: CODE_API_SETUP_ERR,
          },
        )
      }
      retError = error
    } else {
      console.error(
        `something unknown went wrong while trying to fetch ${finalAxiosOptions.url}`,
        {
          error,
          code: 'API_ERROR',
          subCode: CODE_API_UNKNOWN_ERROR,
        },
      )
    }
  }

  return [
    retVal,
    retError,
    {
      meta: axiosOptions,
      errorResponse,
    },
  ]
}
